import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  IconButton,
  Pagination,
  Chip,
  Tooltip,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { deleteRequest, getRequest, resetDelete, resetIdGet } from '../../../redux/modules/radiology/slice';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { toast } from 'react-toastify';
import { DeleteConfirmationModal, ViewConfirmationModal } from '../../../utils/modal';
import moment from 'moment';
import SearchInput from '../../../utils/searchInput';
import DicomViewer from '../image-report/DwvComponent';
import { USER_ROLE_DEFAULT } from '../../../constants/Constant';
import Button from '../../../components/common/Button';

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: 12,
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  overflowX: 'auto',
  overflowY: 'visible',
  WebkitOverflowScrolling: 'touch',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.background.subtle || theme.palette.grey[50],
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    padding: '14px 16px',
    whiteSpace: 'nowrap',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const StyledTableBody = styled(TableBody)(({ theme }) => ({
  '& .MuiTableRow-root': {
    transition: 'background-color 150ms ease',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  '& .MuiTableCell-body': {
    padding: '14px 16px',
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const statusColorMap = {
  'Appointment Miss': { bg: 'rgba(239, 68, 68, 0.1)', color: '#EF4444', border: '#EF4444' },
  'Appointment Complete': { bg: 'rgba(16, 185, 129, 0.1)', color: '#10B981', border: '#10B981' },
  'report_complete': { bg: 'rgba(16, 185, 129, 0.1)', color: '#10B981', border: '#10B981' },
  'images_received': { bg: 'rgba(59, 130, 246, 0.1)', color: '#3B82F6', border: '#3B82F6' },
  'report_pending': { bg: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B', border: '#F59E0B' },
  'in_progress': { bg: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B', border: '#F59E0B' },
  'Requested': { bg: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B', border: '#F59E0B' },
  'scheduled': { bg: 'rgba(107, 114, 128, 0.1)', color: '#6B7280', border: '#6B7280' },
  'Pending': { bg: 'rgba(107, 114, 128, 0.1)', color: '#6B7280', border: '#6B7280' },
};

// Map internal status to display label
const getStatusDisplayLabel = (status) => {
  const statusMap = {
    'scheduled': 'Scheduled',
    'in_progress': 'In Progress',
    'images_received': 'Images Received',
    'report_pending': 'Report Pending',
    'report_complete': 'Report Complete',
    'Requested': 'Requested',
    'Appointment Complete': 'Appointment Complete',
    'Appointment Miss': 'Appointment Miss',
  };
  return statusMap[status] || status;
};

const StatusChip = styled(Chip)(({ theme, statustype }) => {
  const colorSet = statusColorMap[statustype] || statusColorMap['Pending'];
  return {
    backgroundColor: colorSet.bg,
    color: colorSet.color,
    borderColor: colorSet.border,
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 26,
    borderWidth: 1,
    borderStyle: 'solid',
  };
});

const ActionButton = styled(IconButton)(({ theme, buttoncolor }) => {
  const colors = {
    primary: { bg: 'rgba(105, 203, 249, 0.08)', hover: 'rgba(105, 203, 249, 0.15)', icon: '#69CBF9' },
    error: { bg: 'rgba(239, 68, 68, 0.08)', hover: 'rgba(239, 68, 68, 0.15)', icon: '#EF4444' },
    default: { bg: 'rgba(107, 114, 128, 0.08)', hover: 'rgba(107, 114, 128, 0.15)', icon: '#6B7280' },
  };
  const colorSet = colors[buttoncolor] || colors.default;
  return {
    width: 32,
    height: 32,
    borderRadius: 8,
    marginRight: 4,
    backgroundColor: colorSet.bg,
    color: colorSet.icon,
    '&:hover': {
      backgroundColor: colorSet.hover,
    },
  };
});

const EmptyState = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(8),
  color: theme.palette.text.secondary,
}));

const PaginationWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
  '& .MuiPagination-ul': {
    '& .MuiPaginationItem-root': {
      borderRadius: 8,
      fontWeight: 500,
    },
  },
}));

const HeaderRow = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
  },
}));

const tableHeading = ["Appointment Date & Time", "Order Id", "Patient Name", "Exams", "Status", "Category", "Action"];

let deleteItemId;
let limit = 10;
let pageCount = 0;

export default function RadiologyList({ setItemData, toggleList, setPreviewRadiology }) {
  const dispatch = useDispatch();
  let { getRes, deleteRes, loading } = useSelector(state => state.radiology);
  let { currentBranch } = useSelector(state => state.branch);
  let { userData } = useSelector(state => state.auth);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [previewReprt, setPreviewReport] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [appointmentCompleteDescription, setAppointmentCompleteDescription] = useState("");

  const isPatient = userData.role === USER_ROLE_DEFAULT.PATIENT;
  const isAdmin = userData.role === USER_ROLE_DEFAULT.ADMIN;

  const filteredTableHeadings = !isPatient
    ? tableHeading
    : tableHeading.filter(column =>
      column !== 'Patient Name' && column !== 'Status' && column !== 'Category'
    );

  const filteredElem = !isPatient
    ? getRes?.data
    : getRes && getRes?.data?.filter((data) => data?.orderId?.status === "Appointment Complete");

  useEffect(() => {
    if (!currentBranch) { return; }
    dispatch(getRequest({ page, limit, searchQuery }));
  }, [dispatch, currentBranch, page, searchQuery]);

  useEffect(() => {
    if (!deleteRes) { return; }
    if (deleteRes?.success) {
      dispatch(getRequest({ page, limit }));
    }
    toast(deleteRes?.message);
    dispatch(resetDelete());
  }, [dispatch, deleteRes, page]);

  if (getRes?.dataCount && getRes?.dataCount > limit) {
    pageCount = getRes?.dataCount / limit;
    if (pageCount % 2 !== 0) {
      pageCount = Math.ceil(pageCount);
    }
  }

  const renderSkeleton = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          {filteredTableHeadings.map((_, cellIndex) => (
            <TableCell key={cellIndex}>
              <Skeleton variant="text" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );

  const renderingRow = (elem) => {
    if (elem.isReffered && elem.orderId === null) {
      return null;
    }

    return (
      <TableRow key={elem._id}>
        <TableCell>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {moment(elem?.appoinmentId?.startTime).format("MMM DD, YYYY")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {moment(elem?.appoinmentId?.startTime).format("hh:mm A")}
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              bgcolor: 'background.subtle',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              display: 'inline-block',
            }}
          >
            {elem?.orderId?.orderId || '—'}
          </Typography>
        </TableCell>
        {!isPatient && (
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  bgcolor: 'primary.light',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'primary.dark',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                }}
              >
                {elem?.patientId?.fName?.[0]?.toUpperCase()}{elem?.patientId?.lName?.[0]?.toUpperCase()}
              </Box>
              <Typography fontWeight={500}>
                {elem?.patientId?.fName} {elem?.patientId?.lName}
              </Typography>
            </Box>
          </TableCell>
        )}
        <TableCell>
          <Typography variant="body2" sx={{ maxWidth: 200 }}>
            {elem.examList
              .map((item) => item.list.map((listItem) => listItem?.name))
              .flat()
              .join(", ")}
          </Typography>
        </TableCell>
        {!isPatient && (
          <TableCell>
            <StatusChip
              label={getStatusDisplayLabel(elem?.orderId?.status) || 'Pending'}
              statustype={elem?.orderId?.status}
              size="small"
              variant="outlined"
            />
          </TableCell>
        )}
        {!isPatient && (
          <TableCell>
            <Typography variant="body2" color="text.secondary">
              {elem?.appoinmentId?.appoinmentCategory || '—'}
            </Typography>
          </TableCell>
        )}
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title="View Details">
              <ActionButton
                buttoncolor="default"
                size="small"
                onClick={() => {
                  setItemData({ id: elem._id });
                  if (!isPatient) {
                    setPreviewRadiology(true);
                  } else {
                    setAppointmentCompleteDescription(elem.orderId.appoinmentCompleteDescription);
                    setIsViewModalOpen(true);
                  }
                }}
              >
                <VisibilityIcon fontSize="small" />
              </ActionButton>
            </Tooltip>
            {!isPatient && (
              <Tooltip title="Edit Order">
                <ActionButton
                  buttoncolor="primary"
                  size="small"
                  onClick={() => {
                    setItemData({ id: elem._id, gender: elem?.patientId?.gender });
                    toggleList();
                  }}
                >
                  <EditIcon fontSize="small" />
                </ActionButton>
              </Tooltip>
            )}
            {isAdmin && (
              <Tooltip title="Delete Order">
                <ActionButton
                  buttoncolor="error"
                  size="small"
                  onClick={() => {
                    deleteItemId = elem._id;
                    setIsModalOpen(true);
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </ActionButton>
              </Tooltip>
            )}
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <>
      {!previewReprt && (
        <>
          <HeaderRow direction="row" justifyContent="space-between" alignItems="center">
            <SearchInput setSearchQuery={(searchValue) => { setSearchQuery(searchValue); setPage(1); }} />
            {isAdmin && (
              <Button
                variant="primary"
                onClick={() => { toggleList(); setItemData({}); dispatch(resetIdGet()); }}
                startIcon={<AddIcon />}
              >
                Add Order
              </Button>
            )}
          </HeaderRow>

          <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
          <StyledTableContainer component={Paper}>
            <Table sx={{ minWidth: { xs: 600, md: 700 } }} aria-label="study orders table">
              <StyledTableHead>
                <TableRow>
                  {filteredTableHeadings.map(elem => (
                    <TableCell key={elem}>{elem}</TableCell>
                  ))}
                </TableRow>
              </StyledTableHead>
              <StyledTableBody>
                {loading ? (
                  renderSkeleton()
                ) : filteredElem?.length > 0 ? (
                  filteredElem.map(elem => renderingRow(elem))
                ) : (
                  <TableRow>
                    <TableCell colSpan={filteredTableHeadings?.length}>
                      <EmptyState>
                        <AssignmentIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
                        <Typography variant="h6" fontWeight={500} color="text.secondary">
                          No Study Orders Found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {searchQuery ? 'Try adjusting your search criteria' : 'Create a new order to get started'}
                        </Typography>
                      </EmptyState>
                    </TableCell>
                  </TableRow>
                )}
              </StyledTableBody>
            </Table>
          </StyledTableContainer>
          </Box>

          {(getRes?.dataCount > limit) && (
            <PaginationWrapper>
              <Pagination
                count={pageCount}
                page={page}
                color="primary"
                shape="rounded"
                onChange={(event, value) => { setPage(value); }}
              />
            </PaginationWrapper>
          )}

          <DeleteConfirmationModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={() => { dispatch(deleteRequest(deleteItemId)); setIsModalOpen(false); }}
          />
          <ViewConfirmationModal
            description={appointmentCompleteDescription}
            open={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            onConfirm={() => { setIsViewModalOpen(false); }}
          />
        </>
      )}
      {previewReprt && (
        <DicomViewer patient={selectedPatient} onClose={() => setPreviewReport(false)} />
      )}
    </>
  );
}
